"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  Input,
} from "@resume-studio/ui";
import { motion } from "framer-motion";
import {
  HelpCircle,
  Search,
  FileText,
  CreditCard,
  Shield,
  Zap,
  Users,
  Download,
} from "lucide-react";
import Link from "next/link";
import React, { useState } from "react";

type FAQCategory = {
  category: string;
  icon: React.ReactNode;
  questions: FAQQuestion[];
};

type FAQQuestion = {
  id: string;
  question: string;
  answer: string | React.ReactNode;
};

const faqData: FAQCategory[] = [
  {
    category: "Getting Started",
    icon: <Zap className="w-5 h-5" />,
    questions: [
      {
        id: "free-use",
        question: "Is ResumeStudio really free to use?",
        answer:
          "Yes! ResumeStudio offers a free plan that allows you to create, customize, and save your resume. Free users can download their resumes as plain text files. PDF downloads and additional premium features are available with our Pro plan. No credit card is required to get started.",
      },
      {
        id: "time-to-create",
        question: "How long does it take to create a resume?",
        answer:
          "Most users complete their resume in under 10 minutes using our guided process. Our AI-powered content suggestions and pre-written examples help speed up the writing process. You can always save your progress and return later to make edits.",
      },
      {
        id: "no-experience",
        question: "Can I create a resume if I have no work experience?",
        answer:
          "Absolutely! ResumeStudio is perfect for students, recent graduates, and career changers. Our templates include sections for education, skills, projects, volunteer work, and extracurricular activities. Our AI suggestions help highlight your potential even without traditional work experience.",
      },
      {
        id: "account-required",
        question: "Do I need to create an account?",
        answer:
          "You can start building your resume without an account using our 'Try Free' feature. However, creating a free account allows you to save your progress, create multiple resumes, and access your documents from any device. Sign up takes less than 30 seconds.",
      },
    ],
  },
  {
    category: "Templates & Design",
    icon: <FileText className="w-5 h-5" />,
    questions: [
      {
        id: "ats-friendly",
        question: "Are the templates ATS-friendly?",
        answer: (
          <>
            Yes, all our templates are designed to pass Applicant Tracking Systems (ATS) while
            maintaining a professional appearance. We follow ATS best practices including:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Clean, structured formatting</li>
              <li>Standard section headings</li>
              <li>No complex graphics or tables</li>
              <li>Readable fonts and proper spacing</li>
              <li>Keyword optimization tools</li>
            </ul>
          </>
        ),
      },
      {
        id: "template-count",
        question: "How many templates are available?",
        answer:
          "We offer 24 professionally designed templates - 12 for resumes and 12 for cover letters. Each template is customizable with different color schemes, fonts, and layouts. New templates are added regularly based on user feedback and industry trends.",
      },
      {
        id: "customize-templates",
        question: "Can I customize the templates?",
        answer:
          "Yes! Every template is fully customizable. You can change colors, fonts, spacing, section order, and add or remove sections. Our drag-and-drop editor makes it easy to personalize your resume while maintaining professional formatting.",
      },
      {
        id: "mobile-friendly",
        question: "Can I edit my resume on mobile devices?",
        answer:
          "Yes, ResumeStudio is fully responsive and works on all devices. You can create and edit your resume on smartphones, tablets, and desktops. Your work automatically syncs across all devices when you're logged into your account.",
      },
    ],
  },
  {
    category: "Features & Tools",
    icon: <Shield className="w-5 h-5" />,
    questions: [
      {
        id: "import-resume",
        question: "Can I import my existing resume?",
        answer:
          "Yes, you can import existing resumes from PDF or plain text files. Our parser extracts your information and populates it into our templates. Word document (DOC/DOCX) import is coming soon. You can also manually copy and paste content from any source.",
      },
      {
        id: "ai-features",
        question: "What AI features are included?",
        answer: (
          <>
            Our AI-powered features help you create better resumes faster:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Smart content suggestions for job descriptions</li>
              <li>Resume optimization for specific job postings</li>
              <li>Grammar and spell checking</li>
              <li>Action verb recommendations</li>
              <li>Keyword optimization for ATS</li>
              <li>Cover letter generation based on your resume</li>
            </ul>
          </>
        ),
      },
      {
        id: "multiple-resumes",
        question: "Can I create multiple resumes?",
        answer:
          "Yes! You can create multiple resumes tailored for different job applications. Free users can create up to 3 resumes, while Pro users get unlimited resumes and cover letters. This allows you to customize your application for each opportunity.",
      },
      {
        id: "collaboration",
        question: "Can I share my resume for feedback?",
        answer:
          "Yes, you can generate a shareable link to get feedback from friends, mentors, or career counselors. They can view your resume without needing an account. Pro users can also download resumes as PDFs to share via email or other platforms.",
      },
    ],
  },
  {
    category: "Downloading & Formats",
    icon: <Download className="w-5 h-5" />,
    questions: [
      {
        id: "file-formats",
        question: "What file formats can I download?",
        answer:
          "Free plan users can download resumes as plain text (.txt) files. Pro plan users can download high-quality PDF files optimized for both digital viewing and printing. Word document export is on our roadmap for future release.",
      },
      {
        id: "edit-after-download",
        question: "Can I edit my resume after downloading?",
        answer:
          "Yes! Your resumes are saved in your account and can be edited anytime. Changes are automatically saved as you work. You can download updated versions whenever needed, and there's no limit on how many times you can download.",
      },
      {
        id: "print-quality",
        question: "Are the PDFs suitable for printing?",
        answer:
          "Absolutely! Pro plan PDFs are high-resolution and optimized for professional printing. They maintain consistent formatting across different PDF viewers and printers. The files are also optimized for email attachments with reasonable file sizes.",
      },
      {
        id: "language-support",
        question: "Does ResumeStudio support multiple languages?",
        answer:
          "Currently, ResumeStudio primarily supports English resumes. However, you can input content in any language that uses the Latin alphabet. Full internationalization with template translations and language-specific formatting is planned for future updates.",
      },
    ],
  },
  {
    category: "Pricing & Plans",
    icon: <CreditCard className="w-5 h-5" />,
    questions: [
      {
        id: "pro-features",
        question: "What do I get with ResumeStudio Pro?",
        answer: (
          <>
            ResumeStudio Pro includes:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Unlimited resumes and cover letters</li>
              <li>PDF downloads (high-quality)</li>
              <li>All premium templates</li>
              <li>Advanced AI features</li>
              <li>Priority customer support</li>
              <li>No ResumeStudio branding</li>
              <li>Resume analytics and tracking</li>
            </ul>
          </>
        ),
      },
      {
        id: "payment-methods",
        question: "What payment methods do you accept?",
        answer:
          "We accept all major credit cards (Visa, MasterCard, American Express), debit cards, and PayPal through our secure payment processor, Stripe. All payments are encrypted and we never store your payment information.",
      },
      {
        id: "cancel-subscription",
        question: "Can I cancel my subscription anytime?",
        answer:
          "Yes, you can cancel your Pro subscription at any time from your account settings. You'll continue to have access to Pro features until the end of your current billing period. No questions asked, and no cancellation fees.",
      },
      {
        id: "refund-policy",
        question: "Do you offer refunds?",
        answer:
          "Yes, we offer a 7-day money-back guarantee for Pro subscriptions. If you're not satisfied with ResumeStudio Pro, contact our support team within 7 days of purchase for a full refund. We want you to be completely happy with your experience.",
      },
    ],
  },
  {
    category: "Privacy & Security",
    icon: <Shield className="w-5 h-5" />,
    questions: [
      {
        id: "data-privacy",
        question: "Is my personal information safe?",
        answer:
          "Absolutely. We take your privacy seriously. Your data is encrypted, stored securely, and never shared with third parties without your permission. We comply with GDPR and other privacy regulations. You can delete your account and all associated data at any time.",
      },
      {
        id: "who-sees-resume",
        question: "Who can see my resumes?",
        answer:
          "Only you can see your resumes by default. They are private to your account unless you specifically share them using our sharing feature. We never make your resumes public or share them with employers or recruiters without your explicit consent.",
      },
      {
        id: "data-deletion",
        question: "Can I delete my data?",
        answer:
          "Yes, you have full control over your data. You can delete individual resumes or your entire account at any time. When you delete your account, all your personal information and resumes are permanently removed from our servers within 30 days.",
      },
      {
        id: "secure-payments",
        question: "Are payments secure?",
        answer:
          "Yes, all payments are processed through Stripe, a PCI-compliant payment processor used by millions of businesses. We never see or store your credit card information. All transactions are encrypted and secure.",
      },
    ],
  },
  {
    category: "Support & Help",
    icon: <Users className="w-5 h-5" />,
    questions: [
      {
        id: "customer-support",
        question: "How can I get help if I'm stuck?",
        answer: (
          <>
            We offer multiple support channels:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Comprehensive help documentation</li>
              <li>Video tutorials for common tasks</li>
              <li>Email support (support@resumestudio.io)</li>
              <li>Live chat during business hours (Pro users)</li>
              <li>Community forum for tips and advice</li>
            </ul>
            Most inquiries receive a response within 24 hours.
          </>
        ),
      },
      {
        id: "feature-requests",
        question: "Can I suggest new features?",
        answer:
          "We love hearing from our users! You can submit feature requests through our feedback form in your account dashboard or email us at feedback@resumestudio.io. We regularly review suggestions and implement the most requested features.",
      },
      {
        id: "resume-tips",
        question: "Do you provide resume writing tips?",
        answer:
          "Yes! Our blog features regular articles on resume writing, job search strategies, and career advice. Pro users also get access to our exclusive resume writing guide and video tutorials from career experts.",
      },
      {
        id: "business-accounts",
        question: "Do you offer plans for schools or businesses?",
        answer:
          "Yes, we offer special pricing for educational institutions, career centers, and businesses. Contact us at enterprise@resumestudio.io for custom plans that include bulk accounts, training, and dedicated support.",
      },
    ],
  },
];

export const FAQPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredFaqs = faqData
    .map((category) => ({
      ...category,
      questions: category.questions.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (typeof faq.answer === "string" &&
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    }))
    .filter((category) => {
      if (selectedCategory) return category.category === selectedCategory;
      return category.questions.length > 0;
    });

  const scrollToCategory = (categoryName: string) => {
    setSelectedCategory(categoryName);
    const element = document.getElementById(`category-${categoryName.replace(/\s+/g, "-")}`);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            Help Center
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about ResumeStudio. Can't find what you're looking for?{" "}
            <Link href="/contact" className="text-primary hover:underline">
              Contact our support team
            </Link>
            .
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  className="pl-12 pr-4 py-4 text-lg bg-gray-50 border-gray-200 focus:bg-white"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedCategory(null);
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Grid */}
        {!searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12"
          >
            {faqData.map((category, index) => (
              <motion.button
                key={category.category}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`p-6 bg-white rounded-xl border-2 text-left transition-all ${
                  selectedCategory === category.category
                    ? "border-primary shadow-lg"
                    : "border-gray-200 hover:border-primary/50 hover:shadow-md"
                }`}
                onClick={() => scrollToCategory(category.category)}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg text-primary">{category.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{category.category}</h3>
                    <p className="text-sm text-gray-500">{category.questions.length} questions</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* FAQ Sections */}
        <div className="space-y-8">
          {filteredFaqs.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              id={`category-${category.category.replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * categoryIndex }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">{category.icon}</div>
                <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
              </div>

              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="space-y-4">
                    {category.questions.map((faq) => (
                      <AccordionItem
                        key={faq.id}
                        value={faq.id}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-primary py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600 leading-relaxed pb-4">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredFaqs.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-gray-100 p-12 text-center">
              <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">No results found</h2>
              <p className="text-gray-600 mb-6">
                We couldn't find any questions matching "{searchQuery}". Try searching with
                different keywords.
              </p>
              <Button variant="outline" onClick={() => setSearchQuery("")} className="border-2">
                Clear Search
              </Button>
            </Card>
          </motion.div>
        )}

        {/* Still Need Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16"
        >
          <Card className="border-0 shadow-xl bg-gradient-to-r from-primary/5 to-blue-500/5">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Still need help?</h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Can't find the answer you're looking for? Our support team is ready to help you
                create the perfect resume.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/contact">
                  <Button size="lg" className="shadow-lg hover:shadow-xl transition-all">
                    Contact Support
                  </Button>
                </Link>
                <Link href={`${process.env.NEXT_PUBLIC_APP_URL}/try`}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 hover:border-primary"
                  >
                    Start Building Resume
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
